import { Modal, TextInput, Select, Group, Button, Stack, Textarea, NumberInput, ColorPicker, Text, Image, Switch, ScrollArea } from '@mantine/core';
import { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createSetting, updateSetting } from '../../../../redux/reducers/settingSlice';
import { Setting, SettingKey, SettingCategory, GmailSmtpConfig, VivaPaymentConfigMain, SettingType, GoogleMapsType, CardlinkPaymentConfigMain } from '../../../../models/setting.model';
import { updateGlobalMaxCapacity } from '../../../../redux/reducers/showSlice';
import { IconEye, IconEyeOff } from '@tabler/icons-react';
import { ActionIcon } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { selectUserInfo } from '../../../../redux/selectors/authSelector';
import { Logger } from 'utils/logger';

interface AddEditSettingProps {
  opened: boolean;
  onClose: () => void;
  activeSetting: Setting | null;
}

function AddEditSetting({ opened, onClose, activeSetting }: AddEditSettingProps) {
  const dispatch = useDispatch();
  const isMobile = useMediaQuery('(max-width: 50em)');
  const authUser = useSelector(selectUserInfo);
  const emptySetting: Partial<Setting> = {
    setting_key: '',
    setting_value: '',
    setting_type: '',
    description: '',
    category: ''
  };
  const [setting, setSetting] = useState<Partial<Setting>>(emptySetting);

  const categories = Object.values(SettingCategory).map(category => {
    return {
      value: category.toString(),
      label: category.charAt(0).toUpperCase() + category.slice(1).toLowerCase()
    }
  });

  const isColorSetting = useMemo(() => {
    return setting.setting_key === SettingKey.AVAILABLE_SHOW_COLOR ||
      setting.setting_key === SettingKey.ALMOST_FULL_SHOW_COLOR ||
      setting.setting_key === SettingKey.FULL_SHOW_COLOR;
  }, [setting.setting_key]);

  const isGoogleMapsTypeSetting = useMemo(() => {
    return setting.setting_key === SettingKey.GOOGLE_MAPS_TYPE;
  }, [setting.setting_key]);

  const isGmailConfig = useMemo(() => {
    return setting.setting_key === SettingKey.GMAIL_SMTP_CONFIGURATION;
  }, [setting.setting_key]);

  const isVivaConfig = useMemo(() => {
    return setting.setting_key === SettingKey.VIVA_PAYMENT_CONFIGURATION;
  }, [setting.setting_key]);

  const isCardlinkConfig = useMemo(() => {
    return setting.setting_key === SettingKey.CARDLINK_PAYMENT_CONFIGURATION;
  }, [setting.setting_key]);

  const [gmailConfig, setGmailConfig] = useState<GmailSmtpConfig>({
    service: 'gmail',
    secure: true,
    smtp_from: '',
    theater_email: '',
    auth: {
      user: '',
      pass: ''
    }
  });

  const [vivaConfig, setVivaConfig] = useState<VivaPaymentConfigMain>({
    demo: {
      source_code: '',
      api_url: '',
      auth_url: '',
      payment_url: '',
      client_id: '',
      client_secret: '',
      default_payment_method_code: '',
      checkout_color: '',
      viva_connect_url: '',
      viva_create_orders_url: '',
      viva_web_checkout_url: '',
      viva_api_orders_url: '',
      merchant_id: '',
      api_key: ''
    },
    production: {
      source_code: '',
      api_url: '',
      auth_url: '',
      payment_url: '',
      client_id: '',
      client_secret: '',
      default_payment_method_code: '',
      checkout_color: '',
      viva_connect_url: '',
      viva_create_orders_url: '',
      viva_web_checkout_url: '',
      viva_api_orders_url: '',
      merchant_id: '',
      api_key: ''
    },
    activeConfig: 'demo'
  });

  const [cardlinkConfig, setCardlinkConfig] = useState<CardlinkPaymentConfigMain>({
    demo: {
      merchant_id: '',
      shared_secret: '',
      post_url: 'https://ecommerce-test.cardlink.gr/vpos/shophandlermpi',
      currency: '978',
      confirm_url: '',
      cancel_url: '',
      business_partner: 'cardlink',
      language: 'el',
      payment_method: '1',
      installments: '1'
    },
    production: {
      merchant_id: '',
      shared_secret: '',
      post_url: 'https://ecommerce.cardlink.gr/vpos/shophandlermpi',
      currency: '978',
      confirm_url: '',
      cancel_url: '',
      business_partner: 'cardlink',
      language: 'el',
      payment_method: '1',
      installments: '1'
    },
    activeConfig: 'demo'
  });

  // Add state for password
  const [showGmailPassword, setShowGmailPassword] = useState(false);
  const [showVivaSecrets, setShowVivaSecrets] = useState({
    demo: false,
    production: false
  });
  const [showCardlinkSecrets, setShowCardlinkSecrets] = useState({
    demo: false,
    production: false
  });
  const [showAtlasUri, setShowAtlasUri] = useState(false);

  const handleSubmit = () => {
    if (setting.setting_key && setting.setting_value !== undefined && setting.setting_value !== null) {
      let finalSettingValue = setting.setting_value;

      if (isGmailConfig) {
        finalSettingValue = JSON.stringify(gmailConfig);
      } else if (isVivaConfig) {
        finalSettingValue = JSON.stringify(vivaConfig);
      } else if (isCardlinkConfig) {
        finalSettingValue = JSON.stringify(cardlinkConfig);
      }

      if (activeSetting) {
        // Update global max capacity
        if (setting.setting_key === SettingKey.DEFAULT_MAX_CAPACITY) {
          const oldValue = Number(activeSetting?.setting_value || 0);
          const newValue = Number(setting.setting_value);

          // Update global max capacity if it has changed
          if (oldValue !== newValue) {
            dispatch(updateGlobalMaxCapacity({
              maxCapacity: newValue,
            } as any));

            Logger.info(`Global max capacity updated to ${newValue} by User ${authUser?._id} - ${authUser?.first_name} ${authUser?.last_name}`, {
              oldValue,
              newValue
            });
          }
        } else {
          // Update other settings
          dispatch(updateSetting({
            key: activeSetting.setting_key,
            value: finalSettingValue,
            description: setting.description,
            category: setting.category,
            setting_type: setting.setting_type
          } as any));

          Logger.info(`Setting ${activeSetting.setting_key} updated to ${finalSettingValue} by User ${authUser?._id} - ${authUser?.first_name} ${authUser?.last_name}`, {
            settingKey: activeSetting.setting_key,
            settingValue: finalSettingValue
          });
        }
      } else {
        dispatch(createSetting({
          ...setting,
          setting_value: finalSettingValue
        } as any));

          Logger.info(`Setting ${setting.setting_key} created by User ${authUser?._id} - ${authUser?.first_name} ${authUser?.last_name}`, {
            settingKey: setting.setting_key,
            settingValue: finalSettingValue
          });
      }
    }
    onClose();
    setSetting(emptySetting);
  };

  useEffect(() => {
    if (activeSetting) {
      if (isGmailConfig) {
        try {
          setGmailConfig(JSON.parse(activeSetting.setting_value as string));
        } catch (e) {
          console.error('Failed to parse Gmail config:', e);
        }
      } else if (isVivaConfig) {
        try {
          setVivaConfig(JSON.parse(activeSetting.setting_value as string));
        } catch (e) {
          console.error('Failed to parse Viva config:', e);
        }
      } else if (isCardlinkConfig) {
        try {
          setCardlinkConfig(JSON.parse(activeSetting.setting_value as string));
        } catch (e) {
          console.error('Failed to parse Cardlink config:', e);
        }
      }
      setSetting({
        setting_key: activeSetting.setting_key,
        setting_value: activeSetting.setting_value,
        description: activeSetting.description,
        category: activeSetting.category,
        setting_type: activeSetting.setting_type
      });
    } else {
      setSetting(emptySetting);
    }
  }, [activeSetting, isGmailConfig, isVivaConfig, isCardlinkConfig]);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={activeSetting ? 'Edit Setting' : 'Add New Setting'}
      size={isVivaConfig ? "xl" : "lg"}
      radius="md"
      fullScreen={isMobile}
      transitionProps={{ transition: 'fade', duration: 200 }}
      scrollAreaComponent={ScrollArea.Autosize}
      removeScrollProps={{ allowPinchZoom: true }}
    >
      <Stack gap="md">
        <TextInput
          label="Setting Key"
          placeholder="Enter setting key"
          value={setting.setting_key}
          onChange={(e) => setSetting({ ...setting, setting_key: e.target.value })}
          required
          disabled={!!activeSetting}
        />
        <Select
          disabled={!!activeSetting}
          label="Setting Type"
          placeholder="Select setting type"
          data={['string', 'number', 'boolean']}
          value={setting.setting_type}
          onChange={(value) => setSetting({ ...setting, setting_type: value || '' })}
        />
        {setting.setting_type === 'number' && (
          <NumberInput
            label="Setting Value"
            placeholder="Enter setting value"
            value={setting.setting_value as number}
            onChange={(e) => setSetting({ ...setting, setting_value: e })}
            required
          />
        )}
        {setting.setting_type === 'boolean' && (
          <Switch
            size="lg"
            label={setting.setting_value ? 'Enabled' : 'Disabled'}
            checked={setting.setting_value as boolean}
            onChange={(e) => setSetting({ ...setting, setting_value: e.target.checked })}
            color={setting.setting_value ? 'green' : 'grey'}
          />
        )}
        {setting.setting_type === 'string' && !isColorSetting && !isGoogleMapsTypeSetting && (
          <TextInput
            label="Setting Value"
            placeholder="Enter setting value"
            value={setting.setting_value as string}
            onChange={(e) => setSetting({ ...setting, setting_value: e.target.value })}
            type={setting.setting_key === SettingKey.ATLAS_BACKUP_URI ? (showAtlasUri ? 'text' : 'password') : 'text'}
            rightSection={setting.setting_key === SettingKey.ATLAS_BACKUP_URI ? (
              <ActionIcon 
                variant="subtle" 
                onClick={() => setShowAtlasUri(!showAtlasUri)}
                size="sm"
              >
                {showAtlasUri ? <IconEyeOff size={16} /> : <IconEye size={16} />}
              </ActionIcon>
            ) : null}
          />
        )}
        {isColorSetting && (
          <ColorPicker
            fullWidth
            mx="auto"
            value={setting.setting_value as string}
            onChange={(value) => setSetting({ ...setting, setting_value: value })}
          />
        )}
        {isGoogleMapsTypeSetting && (
          <Select
            label="Google Maps Type"
            data={Object.values(GoogleMapsType).map((type: string) => ({
              value: type,
              label: type.charAt(0).toUpperCase() + type.slice(1).toLowerCase()
            }))}
            value={setting.setting_value as string}
            onChange={(value) => setSetting({ ...setting, setting_value: value || GoogleMapsType.IFRAME })}
          />
        )}
        {setting.setting_type === SettingType.IMAGE && (
          <Stack>
            <Image src={setting.setting_value as string} height={100} alt="Image" />
          <TextInput
            label="Image URL"
            value={setting.setting_value as string}
              onChange={(e) => setSetting({ ...setting, setting_value: e.target.value })}
            />
          </Stack>
        )}
        <Textarea
          label="Description"
          placeholder="Enter setting description"
          value={setting.description}
          onChange={(e) => setSetting({ ...setting, description: e.target.value })}
        />
        <Select
          label="Category"
          placeholder="Select a category"
          data={categories}
          value={setting.category}
          onChange={(value) => setSetting({ ...setting, category: value || '' })}
        />

        {isGmailConfig && (
          <Stack>
            <TextInput
              label="SMTP From"
              value={gmailConfig.smtp_from}
              onChange={(e) => setGmailConfig({ ...gmailConfig, smtp_from: e.target.value })}
            />
            <TextInput
              label="Theater Email"
              value={gmailConfig.theater_email}
              onChange={(e) => setGmailConfig({ ...gmailConfig, theater_email: e.target.value })}
            />
            <TextInput
              label="Gmail User"
              value={gmailConfig.auth.user}
              onChange={(e) => setGmailConfig({
                ...gmailConfig,
                auth: { ...gmailConfig.auth, user: e.target.value }
              })}
            />
            <TextInput
              label="Gmail Password/App Password"
              type={showGmailPassword ? 'text' : 'password'}
              value={gmailConfig.auth.pass}
              onChange={(e) => setGmailConfig({
                ...gmailConfig,
                auth: { ...gmailConfig.auth, pass: e.target.value }
              })}
              rightSection={
                <ActionIcon 
                  variant="subtle" 
                  onClick={() => setShowGmailPassword(!showGmailPassword)}
                  size="sm"
                >
                  {showGmailPassword ? <IconEyeOff size={16} /> : <IconEye size={16} />}
                </ActionIcon>
              }
            />
          </Stack>
        )}

        {isVivaConfig && (
          <Stack>
            <Select
              label="Active Configuration"
              data={[
                { value: 'demo', label: 'Demo' },
                { value: 'production', label: 'Production' }
              ]}
              value={vivaConfig.activeConfig}
              onChange={(value) => setVivaConfig({ ...vivaConfig, activeConfig: value as 'demo' | 'production' })}
            />

            <Text size="lg" fw={500} mt="md">Demo Configuration</Text>
            <Group grow>
              <TextInput
                label="Source Code"
                value={vivaConfig.demo.source_code}
                onChange={(e) => setVivaConfig({
                  ...vivaConfig,
                  demo: { ...vivaConfig.demo, source_code: e.target.value }
                })}
              />
              <TextInput
                label="Client ID"
                value={vivaConfig.demo.client_id}
                onChange={(e) => setVivaConfig({
                  ...vivaConfig,
                  demo: { ...vivaConfig.demo, client_id: e.target.value }
                })}
              />
            </Group>
            <Group grow>
              <TextInput
                label="API URL"
                value={vivaConfig.demo.api_url}
                onChange={(e) => setVivaConfig({
                  ...vivaConfig,
                  demo: { ...vivaConfig.demo, api_url: e.target.value }
                })}
              />
              <TextInput
                label="Auth URL"
                value={vivaConfig.demo.auth_url}
                onChange={(e) => setVivaConfig({
                  ...vivaConfig,
                  demo: { ...vivaConfig.demo, auth_url: e.target.value }
                })}
              />
            </Group>
            <Group grow>
              <TextInput
                label="Payment URL"
                value={vivaConfig.demo.payment_url}
                onChange={(e) => setVivaConfig({
                  ...vivaConfig,
                  demo: { ...vivaConfig.demo, payment_url: e.target.value }
                })}
              />
              <TextInput
                label="Client Secret"
                type={showVivaSecrets.demo ? 'text' : 'password'}
                value={vivaConfig.demo.client_secret}
                onChange={(e) => setVivaConfig({
                  ...vivaConfig,
                  demo: { ...vivaConfig.demo, client_secret: e.target.value }
                })}
                rightSection={
                  <ActionIcon 
                    variant="subtle" 
                    onClick={() => setShowVivaSecrets(prev => ({ 
                      ...prev, 
                      demo: !prev.demo 
                    }))}
                    size="sm"
                  >
                    {showVivaSecrets.demo ? <IconEyeOff size={16} /> : <IconEye size={16} />}
                  </ActionIcon>
                }
              />
            </Group>
            <Group grow>
              <TextInput
                label="Default Payment Method Code"
                value={vivaConfig.demo.default_payment_method_code}
                onChange={(e) => setVivaConfig({
                  ...vivaConfig,
                  demo: { ...vivaConfig.demo, default_payment_method_code: e.target.value }
                })}
              />
              <TextInput
                label="Checkout Color"
                value={vivaConfig.demo.checkout_color}
                onChange={(e) => setVivaConfig({
                  ...vivaConfig,
                  demo: { ...vivaConfig.demo, checkout_color: e.target.value }
                })}
              />
            </Group>
            <Group grow>
              <TextInput
                label="Viva Connect URL"
                value={vivaConfig.demo.viva_connect_url}
                onChange={(e) => setVivaConfig({
                  ...vivaConfig,
                  demo: { ...vivaConfig.demo, viva_connect_url: e.target.value }
                })}
              />
              <TextInput
                label="Viva Create Orders URL"
                value={vivaConfig.demo.viva_create_orders_url}
                onChange={(e) => setVivaConfig({
                  ...vivaConfig,
                  demo: { ...vivaConfig.demo, viva_create_orders_url: e.target.value }
                })}
              />
            </Group>
            <Group grow>
              <TextInput
                label="Viva Web Checkout URL"
                value={vivaConfig.demo.viva_web_checkout_url}
                onChange={(e) => setVivaConfig({
                  ...vivaConfig,
                  demo: { ...vivaConfig.demo, viva_web_checkout_url: e.target.value }
                })}
              />
              <TextInput
                label="Viva API Orders URL"
                value={vivaConfig.demo.viva_api_orders_url}
                onChange={(e) => setVivaConfig({
                  ...vivaConfig,
                  demo: { ...vivaConfig.demo, viva_api_orders_url: e.target.value }
                })}
              />
            </Group>
            <Group grow>
              <TextInput
                label="Merchant ID"
                value={vivaConfig.demo.merchant_id}
                onChange={(e) => setVivaConfig({
                  ...vivaConfig,
                  demo: { ...vivaConfig.demo, merchant_id: e.target.value }
                })}
              />
              <TextInput
                label="API Key"
                value={vivaConfig.demo.api_key} 
                onChange={(e) => setVivaConfig({
                  ...vivaConfig,
                  demo: { ...vivaConfig.demo, api_key: e.target.value }
                })}
              />
            </Group>

            <Text size="lg" fw={500} mt="md">Production Configuration</Text>
            <Group grow>
              <TextInput
                label="Source Code"
                value={vivaConfig.production.source_code}
                onChange={(e) => setVivaConfig({
                  ...vivaConfig,
                  production: { ...vivaConfig.production, source_code: e.target.value }
                })}
              />
              <TextInput
                label="Client ID"
                value={vivaConfig.production.client_id}
                onChange={(e) => setVivaConfig({
                  ...vivaConfig,
                  production: { ...vivaConfig.production, client_id: e.target.value }
                })}
              />
            </Group>
            <Group grow>
              <TextInput
                label="API URL"
                value={vivaConfig.production.api_url}
                onChange={(e) => setVivaConfig({
                  ...vivaConfig,
                  production: { ...vivaConfig.production, api_url: e.target.value }
                })}
              />
              <TextInput
                label="Auth URL"
                value={vivaConfig.production.auth_url}
                onChange={(e) => setVivaConfig({
                  ...vivaConfig,
                  production: { ...vivaConfig.production, auth_url: e.target.value }
                })}
              />
            </Group>
            <Group grow>
              <TextInput
                label="Payment URL"
                value={vivaConfig.production.payment_url}
                onChange={(e) => setVivaConfig({
                  ...vivaConfig,
                  production: { ...vivaConfig.production, payment_url: e.target.value }
                })}
              />
              <TextInput
                label="Client Secret"
                type={showVivaSecrets.production ? 'text' : 'password'}
                value={vivaConfig.production.client_secret}
                onChange={(e) => setVivaConfig({
                  ...vivaConfig,
                  production: { ...vivaConfig.production, client_secret: e.target.value }
                })}
                rightSection={
                  <ActionIcon 
                    variant="subtle" 
                    onClick={() => setShowVivaSecrets(prev => ({ 
                      ...prev, 
                      production: !prev.production 
                    }))}
                    size="sm"
                  >
                    {showVivaSecrets.production ? <IconEyeOff size={16} /> : <IconEye size={16} />}
                  </ActionIcon>
                }
              />
            </Group>
            <Group grow>
              <TextInput
                label="Default Payment Method Code"
                value={vivaConfig.production.default_payment_method_code}
                onChange={(e) => setVivaConfig({
                  ...vivaConfig,
                  production: { ...vivaConfig.production, default_payment_method_code: e.target.value }
                })}
              />
              <TextInput
                label="Checkout Color"
                value={vivaConfig.production.checkout_color}
                onChange={(e) => setVivaConfig({
                  ...vivaConfig,
                  production: { ...vivaConfig.production, checkout_color: e.target.value }
                })}
              />
            </Group>
            <Group grow>
              <TextInput
                label="Viva Connect URL"
                value={vivaConfig.production.viva_connect_url}
                onChange={(e) => setVivaConfig({
                  ...vivaConfig,
                  production: { ...vivaConfig.production, viva_connect_url: e.target.value }
                })}
              />
              <TextInput
                label="Viva Create Orders URL"
                value={vivaConfig.production.viva_create_orders_url}
                onChange={(e) => setVivaConfig({
                  ...vivaConfig,
                  production: { ...vivaConfig.production, viva_create_orders_url: e.target.value }
                })}
              />
            </Group>
            <Group grow>
              <TextInput
                label="Viva Web Checkout URL"
                value={vivaConfig.production.viva_web_checkout_url}
                onChange={(e) => setVivaConfig({
                  ...vivaConfig,
                  production: { ...vivaConfig.production, viva_web_checkout_url: e.target.value }
                })}
              />
              <TextInput
                label="Viva API Orders URL"
                value={vivaConfig.production.viva_api_orders_url}
                onChange={(e) => setVivaConfig({
                  ...vivaConfig,
                  production: { ...vivaConfig.production, viva_api_orders_url: e.target.value }
                })}
              />
            </Group>
            <Group grow>
              <TextInput
                label="Merchant ID"
                value={vivaConfig.production.merchant_id}
                onChange={(e) => setVivaConfig({
                  ...vivaConfig,
                  production: { ...vivaConfig.production, merchant_id: e.target.value }
                })}
              />
              <TextInput
                label="API Key"
                value={vivaConfig.production.api_key} 
                onChange={(e) => setVivaConfig({
                  ...vivaConfig,
                  production: { ...vivaConfig.production, api_key: e.target.value }
                })}
              />
            </Group>
          </Stack>
        )}

        {isCardlinkConfig && (
          <Stack>
            <Select
              label="Active Configuration"
              data={[
                { value: 'demo', label: 'Demo' },
                { value: 'production', label: 'Production' }
              ]}
              value={cardlinkConfig.activeConfig}
              onChange={(value) => setCardlinkConfig({ ...cardlinkConfig, activeConfig: value as 'demo' | 'production' })}
            />

            <Text size="lg" fw={500} mt="md">Demo Configuration</Text>
            <Group grow>
              <TextInput
                label="Merchant ID"
                value={cardlinkConfig.demo.merchant_id}
                onChange={(e) => setCardlinkConfig({
                  ...cardlinkConfig,
                  demo: { ...cardlinkConfig.demo, merchant_id: e.target.value }
                })}
              />
              <TextInput
                label="Shared Secret"
                type={showCardlinkSecrets.demo ? 'text' : 'password'}
                value={cardlinkConfig.demo.shared_secret}
                onChange={(e) => setCardlinkConfig({
                  ...cardlinkConfig,
                  demo: { ...cardlinkConfig.demo, shared_secret: e.target.value }
                })}
                rightSection={
                  <ActionIcon 
                    variant="subtle" 
                    onClick={() => setShowCardlinkSecrets(prev => ({ 
                      ...prev, 
                      demo: !prev.demo 
                    }))}
                    size="sm"
                  >
                    {showCardlinkSecrets.demo ? <IconEyeOff size={16} /> : <IconEye size={16} />}
                  </ActionIcon>
                }
              />
            </Group>
            <Group grow>
              <TextInput
                label="Post URL"
                value={cardlinkConfig.demo.post_url}
                onChange={(e) => setCardlinkConfig({
                  ...cardlinkConfig,
                  demo: { ...cardlinkConfig.demo, post_url: e.target.value }
                })}
              />
              <TextInput
                label="Currency"
                value={cardlinkConfig.demo.currency}
                onChange={(e) => setCardlinkConfig({
                  ...cardlinkConfig,
                  demo: { ...cardlinkConfig.demo, currency: e.target.value }
                })}
              />
            </Group>
            <Group grow>
              <TextInput
                label="Confirm URL"
                value={cardlinkConfig.demo.confirm_url}
                onChange={(e) => setCardlinkConfig({
                  ...cardlinkConfig,
                  demo: { ...cardlinkConfig.demo, confirm_url: e.target.value }
                })}
              />
              <TextInput
                label="Cancel URL"
                value={cardlinkConfig.demo.cancel_url}
                onChange={(e) => setCardlinkConfig({
                  ...cardlinkConfig,
                  demo: { ...cardlinkConfig.demo, cancel_url: e.target.value }
                })}
              />
            </Group>
            <Group grow>
              <Select
                label="Business Partner"
                data={[
                  { value: 'nexi', label: 'Nexi' },
                  { value: 'worldline', label: 'Worldline' },
                  { value: 'cardlink', label: 'Cardlink' }
                ]}
                value={cardlinkConfig.demo.business_partner}
                onChange={(value) => setCardlinkConfig({
                  ...cardlinkConfig,
                  demo: { ...cardlinkConfig.demo, business_partner: value as 'cardlink' | 'nexi' | 'worldline' }
                })}
              />
              <TextInput
                label="Language"
                value={cardlinkConfig.demo.language}
                onChange={(e) => setCardlinkConfig({
                  ...cardlinkConfig,
                  demo: { ...cardlinkConfig.demo, language: e.target.value }
                })}
              />
            </Group>
            <Group grow>
              <TextInput
                label="Payment Method"
                value={cardlinkConfig.demo.payment_method}
                onChange={(e) => setCardlinkConfig({
                  ...cardlinkConfig,
                  demo: { ...cardlinkConfig.demo, payment_method: e.target.value }
                })}
              />
              <TextInput
                label="Installments"
                value={cardlinkConfig.demo.installments}
                onChange={(e) => setCardlinkConfig({
                  ...cardlinkConfig,
                  demo: { ...cardlinkConfig.demo, installments: e.target.value }
                })}
              />
            </Group>

            <Text size="lg" fw={500} mt="md">Production Configuration</Text>
            <Group grow>
              <TextInput
                label="Merchant ID"
                value={cardlinkConfig.production.merchant_id}
                onChange={(e) => setCardlinkConfig({
                  ...cardlinkConfig,
                  production: { ...cardlinkConfig.production, merchant_id: e.target.value }
                })}
              />
              <TextInput
                label="Shared Secret"
                type={showCardlinkSecrets.production ? 'text' : 'password'}
                value={cardlinkConfig.production.shared_secret}
                onChange={(e) => setCardlinkConfig({
                  ...cardlinkConfig,
                  production: { ...cardlinkConfig.production, shared_secret: e.target.value }
                })}
                rightSection={
                  <ActionIcon 
                    variant="subtle" 
                    onClick={() => setShowCardlinkSecrets(prev => ({ 
                      ...prev, 
                      production: !prev.production 
                    }))}
                    size="sm"
                  >
                    {showCardlinkSecrets.production ? <IconEyeOff size={16} /> : <IconEye size={16} />}
                  </ActionIcon>
                }
              />
            </Group>
            <Group grow>
              <TextInput
                label="Post URL"
                value={cardlinkConfig.production.post_url}
                onChange={(e) => setCardlinkConfig({
                  ...cardlinkConfig,
                  production: { ...cardlinkConfig.production, post_url: e.target.value }
                })}
              />
              <TextInput
                label="Currency"
                value={cardlinkConfig.production.currency}
                onChange={(e) => setCardlinkConfig({
                  ...cardlinkConfig,
                  production: { ...cardlinkConfig.production, currency: e.target.value }
                })}
              />
            </Group>
            <Group grow>
              <TextInput
                label="Confirm URL"
                value={cardlinkConfig.production.confirm_url}
                onChange={(e) => setCardlinkConfig({
                  ...cardlinkConfig,
                  production: { ...cardlinkConfig.production, confirm_url: e.target.value }
                })}
              />
              <TextInput
                label="Cancel URL"
                value={cardlinkConfig.production.cancel_url}
                onChange={(e) => setCardlinkConfig({
                  ...cardlinkConfig,
                  production: { ...cardlinkConfig.production, cancel_url: e.target.value }
                })}
              />
            </Group>
            <Group grow>
              <Select
                label="Business Partner"
                data={[
                  { value: 'nexi', label: 'Nexi' },
                  { value: 'worldline', label: 'Worldline' },
                  { value: 'cardlink', label: 'Cardlink' }
                ]}
                value={cardlinkConfig.production.business_partner}
                onChange={(value) => setCardlinkConfig({
                  ...cardlinkConfig,
                  production: { ...cardlinkConfig.production, business_partner: value as 'cardlink' | 'nexi' | 'worldline' }
                })}
              />
              <TextInput
                label="Language"
                value={cardlinkConfig.production.language}
                onChange={(e) => setCardlinkConfig({
                  ...cardlinkConfig,
                  production: { ...cardlinkConfig.production, language: e.target.value }
                })}
              />
            </Group>
            <Group grow>
              <TextInput
                label="Payment Method"
                value={cardlinkConfig.production.payment_method}
                onChange={(e) => setCardlinkConfig({
                  ...cardlinkConfig,
                  production: { ...cardlinkConfig.production, payment_method: e.target.value }
                })}
              />
              <TextInput
                label="Installments"
                value={cardlinkConfig.production.installments}
                onChange={(e) => setCardlinkConfig({
                  ...cardlinkConfig,
                  production: { ...cardlinkConfig.production, installments: e.target.value }
                })}
              />
            </Group>
          </Stack>
        )}
      </Stack>
      {setting.setting_key === SettingKey.DEFAULT_MAX_CAPACITY && (
        <Text c="orange" size="sm" mt="md">
          * Updating this setting will update the global max capacity for all shows from today onwards.<br />
          ** This is a heavy operation and should be done with caution as it takes time to complete.<br />
          *** Also is recommended to do this operation at the end of the day when there are no shows to avoid any issues.
        </Text>
      )}
      <Group justify="flex-end" mt="md">
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit}>{activeSetting ? 'Update Setting' : 'Create Setting'}</Button>
      </Group>
    </Modal>
  );
}

export default AddEditSetting;
